import { Content } from '@/types/content';
import { getFormattedDateISO } from '@/utils/getFormattedDate';
import { mergeDefined } from '@/utils/mergeDefined';
import { stripHtml } from '@/utils/stripHtml';
import { findAllOfType, findFirstOfType } from 'lib/labrador/content-operations/findBy';
import { CollectionPage, NewsArticle, WithContext } from 'schema-dts';
import {
  CustomSchemaMarkupConfig,
  getBylineUrl,
  getPublishedUrl,
  publisher,
  publishingPrinciples,
  unnamedSourcesPolicy,
} from './utils';

const mapArticlesToSchema = (articles: Content[]): NewsArticle[] => {
  return articles.map((article) => {
    const { data, children } = article ?? {};
    const { byline, title, subtitle, publishedDate, publishedUrl } = data ?? {};

    const articleImage = findFirstOfType(children, 'labradorImage');
    const imageUrl = articleImage?.data?.imageUrl?.desktop ?? '';

    return {
      '@type': 'NewsArticle',
      headline: stripHtml(title),
      description: stripHtml(subtitle),
      datePublished: publishedDate,
      image: imageUrl,
      url: getPublishedUrl(publishedUrl),
      author: {
        '@type': 'Person',
        name: byline,
        url: getBylineUrl(byline),
      },
    };
  });
};

export const getCollectionPageSchema = (
  pageData: Content,
  schema?: Partial<CollectionPage & CustomSchemaMarkupConfig>,
): WithContext<CollectionPage & CustomSchemaMarkupConfig> => {
  const { data, children } = pageData ?? {};
  const { publishedUrl, publishedDatetime, modifiedDatetime, canonical, seoTitle, title, seoDescription, subtitle } =
    data ?? {};

  const url = publishedUrl === '/etikett' ? canonical : getPublishedUrl(publishedUrl);
  const datePublished = getFormattedDateISO(publishedDatetime);
  const dateModified = getFormattedDateISO(modifiedDatetime);
  const articles = findAllOfType(children, 'articleTeaser');

  const collectionPageSchema: Partial<WithContext<CollectionPage & CustomSchemaMarkupConfig>> = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: seoTitle || title || '',
    description: seoDescription || subtitle || '',
    datePublished,
    dateModified,
    url,
    publisher,
    publishingPrinciples,
    unnamedSourcesPolicy,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: mapArticlesToSchema(articles),
    },
  };

  return mergeDefined(collectionPageSchema, schema);
};
